import {range} from '@benchling/js-utils';

import {Pagination} from 'lib/Listings';
import {BaseTypedValueType} from 'notebook/tables/TableConstants';
import {
  BioentityContent,
  ContainerSource,
  PlateMaterialInput,
  PlateMap,
  PlateMapWell,
  ReagentAssignmentGroup,
  ReagentType,
  Source,
} from 'samples/plate-maps/PlateMapConstants';
import {getFlattenedWellIndex} from 'samples/plate-maps/PlateMapUtil';
import {
  DetailViewTransferTableColumnKey,
  FillPlateTransferTableViewMode,
  SharedDetailViewRowContents,
} from 'samples/plate-maps/transfer-table/FillPlateTransferTableConstants';
import {
  getCellValueError,
  getEmptyWellSharedRow,
  getFinalConcentration,
  getMeasurementUnitCellContents,
  getMeasurementValueCellContents,
  getSharedRow,
  getSourceConcentration,
  getTransferQuantity,
} from 'samples/plate-maps/transfer-table/FillPlateTransferTableUtil';
import {MaybeMixedMaterialInputWithError} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationConstants';
import {formatGridCoordinates} from 'samples/SampleUtil';

export function getEmptyDetailViewRow<
  T extends FillPlateTransferTableViewMode.WELL | FillPlateTransferTableViewMode.PLATE_RECORD,
>({
  showMaterialInputColumn,
  viewMode,
  well,
  wellIndex,
}: {
  showMaterialInputColumn: boolean;
  viewMode: T;
  well: PlateMapWell;
  wellIndex: number;
}): SharedDetailViewRowContents<T> {
  return {
    ...getEmptyWellSharedRow({showMaterialInputColumn, viewMode, well}),
    wellIndex,
    isExistingContent: false,
    isEmpty: true,
    containerOrContainableId: `${wellIndex}`,
    containableId: null,
    [DetailViewTransferTableColumnKey.WELL]: {
      text: '',
      typedValue: {
        type: BaseTypedValueType.STRING,
        rawValue: formatGridCoordinates(well.position.row, well.position.column),
      },
    },
    [DetailViewTransferTableColumnKey.SOURCE]: {
      text: '',
      typedValue: {
        type: BaseTypedValueType.JSON,
        jsonValue: {
          reagent: null,
        },
      },
    },
    [DetailViewTransferTableColumnKey.FINAL_QUANTITY]: {
      ...getMeasurementValueCellContents(well.finalQuantity.measurement.value),
      ...getCellValueError(well.finalQuantity.errors.valueError),
    },
    [DetailViewTransferTableColumnKey.FINAL_QUANTITY_UNIT]: {
      ...getMeasurementUnitCellContents(well.finalQuantity.measurement.unit),
      ...getCellValueError(well.finalQuantity.errors.unitsError),
    },
  };
}

export interface DetailRowViewArgs<
  T extends FillPlateTransferTableViewMode.WELL | FillPlateTransferTableViewMode.PLATE_RECORD,
> {
  container: ContainerSource | null;
  containerMergeSpan: number | null;
  materialInput: MaybeMixedMaterialInputWithError | null;
  plateMaterialInputs?: Array<PlateMaterialInput>;
  showMaterialInputColumn: boolean;
  reagent: Source | BioentityContent | null;
  reagentGroupsById: {[id: string]: ReagentAssignmentGroup};
  viewMode: T;
  well: PlateMapWell;
  wellIndex: number;
  wellMergeSpan: number | null;
}

/**
 * Gets the value of each cell in a single row in a detail table view (Well View, Plate Record View).
 * @param assignmentGroup The assignment group to which the reagent belongs
 * @param container When this row represents a content in a container, the container
 * @param containerMergeSpan When this row represents the first content in a container, including a well in
 *                           the plate being filled, the number of contents in that well
 * @param materialInput In a procedure context, the API ID of the material input associated with this row, if
 *                      any, and any errors with that material input association.
 * @param plateMaterialInputs In a procedure context, a list of plate-used material inputs.
 * @param reagent The reagent being viewed. If null, this row represents an empty well.
 * @param showMaterialInputColumn Whether to include a column for configuring material input associations
 * @param viewMode Which detail view is being shown
 * @param well The well the reagent is being transferred into (or is already a content in)
 * @param wellIndex The numeric index of this well
 * @param wellMergeSpan When this row represents the first reagent in a well, the number of reagents in that
 *                      well
 */
export function getDetailViewRow<
  T extends FillPlateTransferTableViewMode.WELL | FillPlateTransferTableViewMode.PLATE_RECORD,
>({
  container,
  containerMergeSpan,
  materialInput,
  plateMaterialInputs,
  reagent,
  reagentGroupsById,
  showMaterialInputColumn,
  viewMode,
  well,
  wellIndex,
  wellMergeSpan,
}: DetailRowViewArgs<T>): SharedDetailViewRowContents<T> {
  if (reagent == null) {
    return getEmptyDetailViewRow({showMaterialInputColumn, viewMode, well, wellIndex});
  }

  const isEmptyContainer = reagent.type === ReagentType.SOURCE_CONTAINER && reagent.contents.length === 0;
  const mergeRowSpan = wellMergeSpan != null ? {mergeRowSpan: wellMergeSpan} : {};

  const transferQuantity =
    container != null ? getTransferQuantity(container, well) : getTransferQuantity(reagent, well);

  const sharedContents = getSharedRow({
    assignmentGroup: reagentGroupsById[reagent.groupId],
    containerMergeSpan,
    finalConcentration: getFinalConcentration(reagent),
    isEmptyContainer,
    materialInput,
    plateMaterialInputs,
    reagentType: container?.type ?? reagent.type,
    showMaterialInputColumn,
    sourceConcentration: getSourceConcentration(reagent),
    sourceId: reagent.id,
    transferQuantity,
    viewMode,
  });

  return {
    ...sharedContents,
    wellIndex,
    isExistingContent: reagent.type === ReagentType.WELL_CONTENT,
    isEmpty: false,
    containerOrContainableId: container?.id ?? reagent.id,
    containableId: isEmptyContainer ? null : reagent.id,
    [DetailViewTransferTableColumnKey.WELL]: {
      text: '',
      typedValue: {
        type: BaseTypedValueType.STRING,
        rawValue: formatGridCoordinates(well.position.row, well.position.column),
      },
      ...mergeRowSpan,
    },
    [DetailViewTransferTableColumnKey.SOURCE]: {
      text: '',
      typedValue: {
        type: BaseTypedValueType.JSON,
        jsonValue: {
          reagent,
        },
      },
    },
    [DetailViewTransferTableColumnKey.FINAL_QUANTITY]: {
      ...getMeasurementValueCellContents(well.finalQuantity.measurement.value),
      ...mergeRowSpan,
      ...getCellValueError(well.finalQuantity.errors.valueError),
    },
    [DetailViewTransferTableColumnKey.FINAL_QUANTITY_UNIT]: {
      ...getMeasurementUnitCellContents(well.finalQuantity.measurement.unit),
      ...mergeRowSpan,
      ...getCellValueError(well.finalQuantity.errors.unitsError),
    },
  };
}

/**
 * Figure out what the list of `DetailRowViewArgs` should be for the given plate map.
 *
 * Detail views often need to inject view-specific _columns_ into each `SharedDetailViewRowContents`; however,
 * the process to figure out what _rows_ need to be shown is the same for all detailed views. This function
 * figures out what rows need to be shown so that callers can append new columns as needed.
 * @param pagination Information about how to page through the selected wells
 * @param plateMap The plate map representing the plate being filled
 * @param plateMaterialInputs In a procedure context, a list of plate-used material inputs.
 * @param reagentGroupsById A mapping of reagent group ids to reagent groups
 * @param selectedWellIndices What indices in the plate are currently selected
 * @param showMaterialInputColumn Whether to include a column for configuring material input associations
 * @param viewMode Which detail view is being shown
 */
export function getDetailViewRowArgs<
  T extends FillPlateTransferTableViewMode.WELL | FillPlateTransferTableViewMode.PLATE_RECORD,
>({
  pagination,
  plateMap,
  plateMaterialInputs,
  reagentGroupsById,
  selectedWellIndices,
  showMaterialInputColumn,
  viewMode,
}: {
  pagination: Pagination;
  plateMap: PlateMap;
  plateMaterialInputs?: Array<PlateMaterialInput>;
  reagentGroupsById: {[id: string]: ReagentAssignmentGroup};
  selectedWellIndices: Set<number>;
  showMaterialInputColumn: boolean;
  viewMode: T;
}): Array<DetailRowViewArgs<T>> {
  const detailRowViewArgs: Array<DetailRowViewArgs<T>> = [];
  const allWellIndicesToDisplay =
    selectedWellIndices.size === 0 ? new Set(range(0, plateMap.wells.length)) : selectedWellIndices;
  // selectedWellIndices may or may not be in order, and may or may not be contiguous on the plate map, so
  // we have to sort them
  const sortedWellIndices = [...allWellIndicesToDisplay].sort((a, b) => a - b);
  const wellIndicesToDisplay = sortedWellIndices.slice(
    pagination.offset,
    Math.min(pagination.offset + pagination.limit, pagination.total)
  );
  for (const index of wellIndicesToDisplay) {
    const well = plateMap.wells[index];
    const wellIndex = getFlattenedWellIndex(well.position.row, well.position.column, plateMap.size);
    // Display all sources, and then all contents.
    const sourcesAndContents = [...well.sources, ...well.contents];
    // We still want to be able to register a dilution even if we don't have any sources or contents in the
    // well, so return a row with a null reagent.
    if (sourcesAndContents.length === 0) {
      detailRowViewArgs.push({
        container: null,
        containerMergeSpan: null,
        materialInput: null,
        plateMaterialInputs,
        showMaterialInputColumn,
        reagent: null,
        reagentGroupsById,
        viewMode,
        well,
        wellIndex,
        wellMergeSpan: null,
      });
      continue;
    }
    const numberOfRowsInWell = sourcesAndContents.reduce((acc, sourceOrContent) => {
      if (sourceOrContent.type === ReagentType.SOURCE_CONTAINER && sourceOrContent.contents.length > 0) {
        return acc + sourceOrContent.contents.length;
      } else {
        return acc + 1;
      }
    }, 0);
    for (const [indexInWell, sourceOrContent] of sourcesAndContents.entries()) {
      const isContent = sourceOrContent.type === ReagentType.WELL_CONTENT;
      const isContainerWithContents =
        sourceOrContent.type === ReagentType.SOURCE_CONTAINER && sourceOrContent.contents.length > 0;
      const reagents = isContainerWithContents ? sourceOrContent.contents : [sourceOrContent];
      for (const [indexInReagent, reagent] of reagents.entries()) {
        // The reagents may not have loaded yet for preexisting plate contents.
        if (reagentGroupsById[reagent.groupId] == null) {
          continue;
        }

        let containerMergeSpan = null;
        if (isContainerWithContents && indexInReagent === 0) {
          containerMergeSpan = sourceOrContent.contents.length;
        } else if (isContent && indexInWell === well.sources.length) {
          // Because we're displaying all sources and then all contents, we know the index of the first well
          // content.
          containerMergeSpan = well.contents.length;
        }

        detailRowViewArgs.push({
          container: isContainerWithContents ? sourceOrContent : null,
          containerMergeSpan,
          materialInput:
            reagent.type === ReagentType.SOURCE_CONTAINER ? null : (reagent.materialInput ?? null),
          plateMaterialInputs,
          showMaterialInputColumn,
          reagent,
          reagentGroupsById,
          viewMode,
          well,
          wellIndex,
          wellMergeSpan:
            indexInWell + indexInReagent === 0 && numberOfRowsInWell > 1 ? numberOfRowsInWell : null,
        });
      }
    }
  }

  return detailRowViewArgs;
}
